import { useState } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { cn } from '@/lib/cn';
import { field, pill } from '@/components/PageKit';

/** Typed names are compared as a person would read them: spacing aside. */
export const sameName = (a: string, b: string) =>
  a.trim().replace(/\s+/g, ' ') === b.trim().replace(/\s+/g, ' ');

/**
 * A destructive action that asks first.
 *
 * The mobile screens put an Alert in front of approving, rejecting, revoking
 * and closing; on the web the same actions would otherwise be one stray click
 * away. The question appears in place rather than in a dialog, so it cannot be
 * dismissed by clicking somewhere harmless-looking.
 *
 * Redesign 2026-10: pills. A danger action is a red OUTLINE until it is
 * confirmed, and the confirming press is solid red beside a quiet «إلغاء».
 * With `detail` or `typeToConfirm` the question opens as a small panel — what
 * goes with it, and (deleting a child, board f5) the name typed out before
 * the red button wakes.
 */
export function ConfirmButton({
  testId, label, question, onConfirm, tone = 'quiet', confirmLabel, detail, typeToConfirm, size = 'md',
  className,
}: {
  testId: string;
  label: string;
  question: string;
  onConfirm: () => void;
  tone?: 'quiet' | 'primary' | 'danger';
  /** The confirming press, when it should say more than the label («احذف»). */
  confirmLabel?: string;
  /** What the action takes with it, said under the question. */
  detail?: string;
  /** A word the reader must type before the confirming press is enabled. */
  typeToConfirm?: { value: string; label: string };
  size?: 'sm' | 'md';
  className?: string;
}) {
  const { t } = useLanguage();
  const [asking, setAsking] = useState(false);
  const [typed, setTyped] = useState('');

  const first = {
    quiet: pill.quiet,
    primary: pill.primary,
    danger: pill.dangerOutline,
  }[tone];
  const confirming = {
    quiet: pill.primary,
    primary: pill.primary,
    danger: pill.danger,
  }[tone];
  const height = size === 'sm' ? pill.sm : pill.md;

  const open = () => { setTyped(''); setAsking(true); };
  const ready = !typeToConfirm || sameName(typed, typeToConfirm.value);

  if (!asking) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={open}
        className={cn('shrink-0', pill.base, height, first, className)}
      >
        {label}
      </button>
    );
  }

  const buttons = (
    <div className="flex shrink-0 flex-wrap items-center gap-2">
      <button
        type="button"
        data-testid={`${testId}-yes`}
        disabled={!ready}
        onClick={() => { if (!ready) return; setAsking(false); onConfirm(); }}
        className={cn(pill.base, height, confirming)}
      >
        {confirmLabel ?? label}
      </button>
      <button
        type="button"
        data-testid={`${testId}-no`}
        onClick={() => setAsking(false)}
        className={cn(pill.base, height, pill.quiet)}
      >
        {t('common.cancel')}
      </button>
    </div>
  );

  // The short form: the question and two pills on one line.
  if (!detail && !typeToConfirm) {
    return (
      <div className={cn('flex shrink-0 flex-wrap items-center gap-2', className)}>
        <span className="text-sm text-ink">{question}</span>
        {buttons}
      </div>
    );
  }

  return (
    <div
      data-testid={`${testId}-panel`}
      className={cn(
        'w-full space-y-3 rounded-[18px] p-4',
        tone === 'danger' ? 'bg-danger/10' : 'bg-surface',
        className,
      )}
    >
      <p className="text-lg font-semibold text-ink">{question}</p>
      {detail && <p className="text-[15px] text-helper">{detail}</p>}
      {typeToConfirm && (
        <label className="block space-y-2">
          <span className="block text-sm font-medium text-ink">{typeToConfirm.label}</span>
          <input
            data-testid={`${testId}-typed`}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={typeToConfirm.value}
            autoComplete="off"
            spellCheck={false}
            className={cn(field, 'max-w-sm')}
          />
        </label>
      )}
      {buttons}
    </div>
  );
}
